// Контракт API пульта (задача T18 плана T): раздел «Тест-бот» — только admin. Тест-бот — внутренний стенд проверки Анны в
// Telegram: сотрудник пишет боту как клиент, Анна отвечает по правилам боя, а пульт показывает, как она рассудила.
//
// Маршруты (все `/api/v1/testbot/*`, `access: 'admin'`):
// - боты: `GET /testbot/bots` → `TestBotList`; `POST /testbot/bots` `{ token, password, dailyBudgetRub?, defaultChannel? }`
//   → 201 `{ bot }`; `PATCH /testbot/bots/:id` `{ enabled?, dailyBudgetRub?, defaultChannel?, autoEval? }` → `{ bot, undoId }`
//   (отмена — `POST /api/v1/undo`, 10 минут); `POST /testbot/bots/:id/password` `{ password, revokeAll }` →
//   `{ bot, revoked }`; `POST /testbot/bots/:id/token` `{ token }` → `{ bot }`; `DELETE /testbot/bots/:id` →
//   `{ ok: true, revoked }`;
// - тестировщики: `GET /testbot/bots/:id/testers` → `{ items }`; `POST /testbot/testers/:tid/revoke` → `{ tester }`;
// - переписки: `GET /testbot/chats?bot&tester&bad=1&cursor&limit` → `{ items, nextCursor }` (свежие сверху);
//   `GET /testbot/chats/:id` → `TestChatDetail`;
// - правки: `GET /testbot/corrections?state=new|saved|dismissed` → `{ items }`; `POST /testbot/turns/:id/example`
//   `{ reply }` → 201 (новый) или 200 (обновлён) `{ exampleId, undoId }`; `PUT /testbot/turns/:id/correction-state`
//   `{ state: 'new' | 'dismissed' }` → `{ turnId, state, stateRu }`;
// - экзамен: `GET /testbot/exam-cases?status=active|archived` → `{ items }`; `PUT /testbot/exam-cases/:id/status`
//   `{ status }` → `{ case }`.
//
// Тайна: токен бота пульт видит только при вводе. Ответы несут `@имя` бота и 4 последних знака токена (`tokenLast4`), ни
// шифра, ни хеша пароля в них нет. Ошибка Telegram в ответ не уходит — только код словами (`TESTBOT_ERRORS_RU`).
//
// Перечисления — копии `src/db/schema-testbot.ts` (T4), подписи каналов — копия `channelKeyLabelRu`, коды и тексты ошибок —
// копия `API_ERROR_STATUS` / `API_ERRORS_RU`; совпадение держит `tests/console/testbot-contract.test.ts`.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';
import { MomentSchema } from './common.js';
import { PathGroupSchema } from './dialog-view.js';
import { BadReasonSchema, ReviewVerdictSchema } from './review.js';

// ───────────────────────────── перечисления ─────────────────────────────

/** Каналы, которые бот умеет изображать (копия `TEST_BOT_CHANNELS` таблиц T4). */
export const TEST_BOT_CHANNELS = ['textback:tg', 'textback:max', 'textback:whatsappb', 'avito', 'telegram_personal'] as const;
export const TestBotChannelSchema = z.enum(TEST_BOT_CHANNELS);
export type TestBotChannel = z.infer<typeof TestBotChannelSchema>;

/** Каналы словами — как их подписывает пульт (`channelKeyLabelRu`). */
export const TEST_BOT_CHANNEL_RU: Readonly<Record<TestBotChannel, string>> = {
  'textback:tg': 'Telegram-бот (TextBack)',
  'textback:max': 'TextBack (max)',
  'textback:whatsappb': 'WhatsApp Business (TextBack)',
  avito: 'Авито',
  telegram_personal: 'Telegram (личный)',
};

/**
 * Состояние бота для людей: `working` — опрос идёт, `starting` — ещё не опрашивали (только подключили или сменили токен),
 * `no_connection` — Telegram не отвечает, `token_revoked` — Telegram отверг токен, `conflict` — тот же токен опрашивает
 * другой процесс, `disabled` — бот выключен в пульте.
 */
export const TEST_BOT_STATES = ['working', 'starting', 'no_connection', 'token_revoked', 'conflict', 'disabled'] as const;
export const TestBotStateSchema = z.enum(TEST_BOT_STATES);
export type TestBotState = z.infer<typeof TestBotStateSchema>;

/** Состояния словами; у `no_connection` в строке бота ещё «с ЧЧ:ММ МСК» (`stateRu`). */
export const TEST_BOT_STATE_RU: Readonly<Record<TestBotState, string>> = {
  working: 'работает',
  starting: 'запускается',
  no_connection: 'нет связи с Telegram',
  token_revoked: 'токен отозван',
  conflict: 'конфликт опроса',
  disabled: 'выключен',
};

/** Почему тестировщика отключили (копия T4). */
export const TEST_TESTER_REVOKE_REASONS = ['logout', 'password_reset', 'console', 'bot_deleted'] as const;
export const TestTesterRevokeReasonSchema = z.enum(TEST_TESTER_REVOKE_REASONS);
export type TestTesterRevokeReason = z.infer<typeof TestTesterRevokeReasonSchema>;

export const TEST_TESTER_REVOKE_REASON_RU: Readonly<Record<TestTesterRevokeReason, string>> = {
  logout: 'вышел сам',
  password_reset: 'сменили пароль бота',
  console: 'отключён в пульте',
  bot_deleted: 'бот удалён',
};

/** Состояние переписки (копия T4). */
export const TEST_CHAT_STATUSES = ['active', 'closed'] as const;
export const TestChatStatusSchema = z.enum(TEST_CHAT_STATUSES);

/** «Часы» переписки (копия T4): настоящие, будний вечер, ночь, суббота. */
export const TEST_CLOCK_MODES = ['now', 'evening', 'night', 'saturday'] as const;
export const TestClockModeSchema = z.enum(TEST_CLOCK_MODES);

/** Кто написал сообщение (копия T4). */
export const TEST_MESSAGE_ROLES = ['client', 'anna'] as const;
export const TestMessageRoleSchema = z.enum(TEST_MESSAGE_ROLES);

/** Состояние хода (копия T4). */
export const TEST_TURN_STATUSES = ['running', 'ready', 'sent', 'partial', 'superseded', 'failed', 'budget', 'skipped'] as const;
export const TestTurnStatusSchema = z.enum(TEST_TURN_STATUSES);
export type TestTurnStatus = z.infer<typeof TestTurnStatusSchema>;

export const TEST_TURN_STATUS_RU: Readonly<Record<TestTurnStatus, string>> = {
  running: 'идёт',
  ready: 'готов, не отправлен',
  sent: 'отправлен',
  partial: 'отправлен не весь',
  superseded: 'заменён новым сообщением',
  failed: 'не получился',
  budget: 'остановлен лимитом бота',
  skipped: 'Анна промолчала',
};

/** Экзаменатор хода (копия T4). */
export const TEST_EVAL_STATUSES = ['none', 'running', 'done', 'failed', 'off'] as const;
export const TestEvalStatusSchema = z.enum(TEST_EVAL_STATUSES);

/** Что сделали с исправлением тестировщика (копия T4). */
export const TEST_CORRECTION_STATES = ['new', 'saved', 'dismissed'] as const;
export const TestCorrectionStateSchema = z.enum(TEST_CORRECTION_STATES);
export type TestCorrectionState = z.infer<typeof TestCorrectionStateSchema>;

export const TEST_CORRECTION_STATE_RU: Readonly<Record<TestCorrectionState, string>> = {
  new: 'ждёт разбора',
  saved: 'сохранено',
  dismissed: 'не в примеры',
};

/** Откуда эталон случая экзамена (копия T4). */
export const TEST_EXAM_REFERENCE_KINDS = ['approved', 'correction'] as const;
export const TestExamReferenceKindSchema = z.enum(TEST_EXAM_REFERENCE_KINDS);
export type TestExamReferenceKind = z.infer<typeof TestExamReferenceKindSchema>;

export const TEST_EXAM_REFERENCE_KIND_RU: Readonly<Record<TestExamReferenceKind, string>> = {
  approved: 'одобренный ответ Анны',
  correction: 'исправление тестировщика',
};

/** Состояние случая экзамена (копия T4). */
export const TEST_EXAM_STATUSES = ['active', 'archived'] as const;
export const TestExamStatusSchema = z.enum(TEST_EXAM_STATUSES);
export type TestExamStatus = z.infer<typeof TestExamStatusSchema>;

export const TEST_EXAM_STATUS_RU: Readonly<Record<TestExamStatus, string>> = {
  active: 'в экзамене',
  archived: 'в архиве',
};

/**
 * Каким был бы чат в бою во время хода (копия `TEST_PROD_MODES` T15, Ruling T-16): тень, ждёт человека (после звонка,
 * заявки или передачи) или Анна закончила (отказ).
 */
export const TEST_PROD_MODES = ['shadow', 'awaiting_human', 'done'] as const;
export const TestProdModeSchema = z.enum(TEST_PROD_MODES);
export type TestProdMode = z.infer<typeof TestProdModeSchema>;

export const TEST_PROD_MODE_RU: Readonly<Record<TestProdMode, string>> = {
  shadow: 'тень',
  awaiting_human: 'чат ждёт человека',
  done: 'Анна закончила чат',
};

/** Финальные действия Анны (копия `FINAL_TOOLS`, `src/replay/sandbox-turn.ts`): после них чат в бою уже не её. */
export const TEST_FINAL_TOOLS = ['call_request', 'lead_to_sales', 'human_handoff', 'lead_reject'] as const;
export type TestFinalTool = (typeof TEST_FINAL_TOOLS)[number];

/** Финальное действие словами (копия `ACTION_RU` экзамена для этих действий). */
export const TEST_FINAL_ACTION_RU: Readonly<Record<TestFinalTool, string>> = {
  call_request: 'номер менеджеру',
  lead_to_sales: 'заявка в продажи',
  human_handoff: 'передала человеку',
  lead_reject: 'отказ',
};

// ───────────────────────────── пределы ─────────────────────────────

/** Пароль входа в бота: от 8 до 128 знаков (копия `PASSWORD_MIN` / `PASSWORD_MAX`, `src/lib/password-hash.ts`). */
export const TEST_PASSWORD_LIMITS = { min: 8, max: 128 } as const;

/** Лимит бота в день, ₽ (CHECK `test_bots_daily_budget_rub_check` и умолчание колонки). */
export const TEST_BOT_BUDGET = { min: 1, max: 100_000, default: 300 } as const;

/** Сколько переписок в списке за раз по умолчанию и самое большее. */
export const TEST_CHATS_PAGE = { default: 50, max: 100 } as const;

// ───────────────────────────── боты ─────────────────────────────

export const TestBotViewSchema = z.object({
  id: z.string().min(1),
  /** `@имя` бота в Telegram — с «@». */
  username: z.string().min(2),
  title: z.string(),
  /** Последние четыре знака токена — узнать бота, не показывая токен. */
  tokenLast4: z.string().min(1).max(4),
  state: TestBotStateSchema,
  /** Состояние словами: «работает», «нет связи с Telegram с 14:20 МСК» … */
  stateRu: z.string().min(1),
  enabled: z.boolean(),
  /** Экзаменатор под каждым ответом. */
  autoEval: z.boolean(),
  dailyBudgetRub: z.number().int(),
  /** Потрачено сегодня (день по МСК), ₽. */
  spentTodayRub: z.number().nonnegative(),
  /** «128 ₽ из 300 ₽ сегодня». */
  spendRu: z.string().min(1),
  defaultChannel: TestBotChannelSchema,
  defaultChannelRu: z.string().min(1),
  /** Сколько тестировщиков сейчас могут писать боту (не отключённых). */
  testers: z.number().int().nonnegative(),
  createdAt: MomentSchema,
  passwordSetAt: MomentSchema,
});
export type TestBotView = z.infer<typeof TestBotViewSchema>;

/** Список ботов (без удалённых). `keyConfigured: false` — ключа шифрования нет: подключать ботов нельзя. */
export const TestBotListSchema = z.object({
  items: z.array(TestBotViewSchema),
  total: z.number().int().nonnegative(),
  keyConfigured: z.boolean(),
});
export type TestBotList = z.infer<typeof TestBotListSchema>;

const BudgetSchema = z.number().int().min(TEST_BOT_BUDGET.min).max(TEST_BOT_BUDGET.max);

export const CreateTestBotBodySchema = z.object({
  token: z.string(),
  password: z.string(),
  dailyBudgetRub: BudgetSchema.optional(),
  defaultChannel: TestBotChannelSchema.optional(),
});
export type CreateTestBotBody = z.infer<typeof CreateTestBotBodySchema>;

export const CreateTestBotResponseSchema = z.object({ bot: TestBotViewSchema });
export type CreateTestBotResponse = z.infer<typeof CreateTestBotResponseSchema>;

export const PatchTestBotBodySchema = z.object({
  enabled: z.boolean().optional(),
  dailyBudgetRub: BudgetSchema.optional(),
  defaultChannel: TestBotChannelSchema.optional(),
  autoEval: z.boolean().optional(),
});
export type PatchTestBotBody = z.infer<typeof PatchTestBotBodySchema>;

export const PatchTestBotResponseSchema = z.object({ bot: TestBotViewSchema, undoId: z.string().min(1) });
export type PatchTestBotResponse = z.infer<typeof PatchTestBotResponseSchema>;

/** `revokeAll` — «Сменить пароль и отключить всех»: все тестировщики бота входят заново. */
export const TestBotPasswordBodySchema = z.object({ password: z.string(), revokeAll: z.boolean() });
export type TestBotPasswordBody = z.infer<typeof TestBotPasswordBodySchema>;

/** `revoked` — сколько тестировщиков отключено этой сменой. */
export const TestBotPasswordResponseSchema = z.object({ bot: TestBotViewSchema, revoked: z.number().int().nonnegative() });
export type TestBotPasswordResponse = z.infer<typeof TestBotPasswordResponseSchema>;

export const TestBotTokenBodySchema = z.object({ token: z.string() });
export type TestBotTokenBody = z.infer<typeof TestBotTokenBodySchema>;

export const TestBotTokenResponseSchema = z.object({ bot: TestBotViewSchema });
export type TestBotTokenResponse = z.infer<typeof TestBotTokenResponseSchema>;

/** Бот удалён: токен стёрт, `revoked` — сколько тестировщиков отключено. */
export const DeleteTestBotResponseSchema = z.object({ ok: z.literal(true), revoked: z.number().int().nonnegative() });
export type DeleteTestBotResponse = z.infer<typeof DeleteTestBotResponseSchema>;

// ───────────────────────────── тестировщики ─────────────────────────────

export const TestTesterViewSchema = z.object({
  id: z.string().min(1),
  botId: z.string().min(1),
  /** Имя и фамилия из Telegram. */
  name: z.string(),
  /** `@ник` в Telegram с «@»; ника нет — `null`. */
  username: z.string().nullable(),
  /** Когда вошёл по паролю. */
  authorizedAt: MomentSchema,
  /** Когда последний раз писал боту. */
  lastSeenAt: MomentSchema.nullable(),
  /** Может писать боту (не отключён). */
  active: z.boolean(),
  revokedAt: MomentSchema.nullable(),
  revokeReason: TestTesterRevokeReasonSchema.nullable(),
  revokeReasonRu: z.string().nullable(),
  /** Сколько ходов оценил (👍 и 👎). */
  reviews: z.number().int().nonnegative(),
});
export type TestTesterView = z.infer<typeof TestTesterViewSchema>;

/** Тестировщики бота: действующие первыми, внутри — по последней активности. */
export const TestTestersResponseSchema = z.object({ items: z.array(TestTesterViewSchema) });
export type TestTestersResponse = z.infer<typeof TestTestersResponseSchema>;

export const RevokeTesterResponseSchema = z.object({ tester: TestTesterViewSchema });
export type RevokeTesterResponse = z.infer<typeof RevokeTesterResponseSchema>;

// ───────────────────────────── переписки ─────────────────────────────

/** Строка списка переписок: кто, когда, сколько сообщений, 👍 и 👎. */
export const TestChatRowSchema = z.object({
  id: z.string().min(1),
  botId: z.string().min(1),
  botUsername: z.string().min(2),
  testerId: z.string().min(1),
  testerName: z.string(),
  testerUsername: z.string().nullable(),
  channelKey: TestBotChannelSchema,
  channelRu: z.string().min(1),
  status: TestChatStatusSchema,
  clockMode: TestClockModeSchema,
  startedAt: MomentSchema,
  lastActivityAt: MomentSchema,
  closedAt: MomentSchema.nullable(),
  /** Сообщений клиента и Анны. */
  messages: z.number().int().nonnegative(),
  /** Оценок «Хорошо» и «Плохо» по ходам переписки. */
  good: z.number().int().nonnegative(),
  bad: z.number().int().nonnegative(),
});
export type TestChatRow = z.infer<typeof TestChatRowSchema>;

/** Страница переписок; `nextCursor` — продолжение («Показать ещё»), `null` — дальше нет. */
export const TestChatListSchema = z.object({ items: z.array(TestChatRowSchema), nextCursor: z.string().nullable() });
export type TestChatList = z.infer<typeof TestChatListSchema>;

/** Вложение сообщения: только вид и имя — сами файлы лежат в Telegram. */
export const TestMessageMediaViewSchema = z.object({ kind: z.string(), name: z.string().nullable() });

/** Пузырь переписки; `at` — момент по «песочным» часам чата, `sentAt` — когда Telegram принял часть ответа Анны. */
export const TestMessageViewSchema = z.object({
  id: z.string().min(1),
  seq: z.number().int(),
  role: TestMessageRoleSchema,
  text: z.string(),
  media: z.array(TestMessageMediaViewSchema),
  at: MomentSchema,
  sentAt: MomentSchema.nullable(),
  /** Ход и номер части у сообщения Анны; у клиента — `null`. */
  turnId: z.string().nullable(),
  partIndex: z.number().int().nullable(),
});
export type TestMessageView = z.infer<typeof TestMessageViewSchema>;

/** Оценка экзаменатора: значок и слово, вердикт и серьёзность (`src/replay/evaluate.ts`), причина и строка целиком. */
export const TestEvaluationViewSchema = z.object({
  icon: z.enum(['✅', '⚠️', '⛔']),
  badgeRu: z.string().min(1),
  verdict: z.string(),
  severity: z.string(),
  reason: z.string(),
  /** Как её видит тестировщик в боте: «Экзаменатор: ✅ хорошо · ответила ✅ · …». */
  textRu: z.string().min(1),
});
export type TestEvaluationView = z.infer<typeof TestEvaluationViewSchema>;

/** Оценка хода тестировщиком. */
export const TestReviewViewSchema = z.object({
  testerId: z.string().min(1),
  testerName: z.string(),
  verdict: ReviewVerdictSchema,
  reasons: z.array(BadReasonSchema),
  note: z.string().nullable(),
  at: MomentSchema,
});
export type TestReviewView = z.infer<typeof TestReviewViewSchema>;

/** Исправление тестировщика — как надо было ответить. */
export const TestCorrectionViewSchema = z.object({
  text: z.string(),
  byName: z.string().nullable(),
  at: MomentSchema.nullable(),
  state: TestCorrectionStateSchema.nullable(),
  stateRu: z.string().nullable(),
});

/**
 * Финальное действие переписки, в силе во время хода: ход шёл боем «после передачи» (Ruling T-7). `labelRu` — «передала
 * человеку (просит менеджера)», «отказ»; `state` — чат ждёт человека или Анна закончила.
 */
export const TestFinalActionViewSchema = z.object({
  tool: z.enum(TEST_FINAL_TOOLS),
  reason: z.string().nullable(),
  state: z.enum(['awaiting_human', 'done']),
  labelRu: z.string().min(1),
});
export type TestFinalActionView = z.infer<typeof TestFinalActionViewSchema>;

/** Ход Анны в переписке: ответ, «Путь», экзаменатор, оценки, исправление; вариант 🔁 — с `variantOf`. */
export const TestTurnViewSchema = z.object({
  id: z.string().min(1),
  variantOf: z.string().nullable(),
  variantNo: z.number().int().positive(),
  status: TestTurnStatusSchema,
  statusRu: z.string().min(1),
  /** Сквозные номера сообщений серии, на которую отвечает ход (`TestMessageView.seq`). */
  firstSeq: z.number().int(),
  lastSeq: z.number().int(),
  parts: z.array(z.string()),
  /** Откуда ответ: модель, переписанный вариант, нейтральный ответ кода … */
  source: z.string().nullable(),
  /** Решение кода (`answer_and_ask`, `to_sales` …). */
  action: z.string().nullable(),
  /** «Что Анна сделала бы»: «📌 Передала бы человеку: …», «⛔ Отказала бы: …». */
  actionLinesRu: z.array(z.string()),
  /** Каким был бы чат в бою во время хода (Ruling T-16). */
  prodMode: TestProdModeSchema,
  prodModeRu: z.string().min(1),
  /** Финальное действие, после которого шёл ход (бой «после передачи»); до финала — `null`. */
  afterFinal: TestFinalActionViewSchema.nullable(),
  /** «Путь» хода группами, как в разборе диалога. */
  path: z.array(PathGroupSchema),
  evalStatus: TestEvalStatusSchema,
  evaluation: TestEvaluationViewSchema.nullable(),
  reviews: z.array(TestReviewViewSchema),
  correction: TestCorrectionViewSchema.nullable(),
  /** Расход хода, $. */
  costUsd: z.number().nonnegative(),
  latencyMs: z.number().int().nullable(),
  release: z.string(),
  promptVersion: z.string().nullable(),
  /** Код сбоя хода (`ModelCallError: …`); не упал — `null`. */
  error: z.string().nullable(),
  /** Когда Анна отправила бы ответ по своему темпу (часы песочницы). */
  wouldSendAt: MomentSchema.nullable(),
  createdAt: MomentSchema,
  finishedAt: MomentSchema.nullable(),
});
export type TestTurnView = z.infer<typeof TestTurnViewSchema>;

/** Карточка переписки: шапка, пузыри по порядку, ходы по времени (варианты — сразу после своего хода). */
export const TestChatDetailSchema = z.object({
  chat: TestChatRowSchema,
  messages: z.array(TestMessageViewSchema),
  turns: z.array(TestTurnViewSchema),
});
export type TestChatDetail = z.infer<typeof TestChatDetailSchema>;

// ───────────────────────────── правки ─────────────────────────────

/** Правка: «клиент → Анна → как надо». `exampleId` — пример, если правку отправили «В примеры». */
export const TestCorrectionRowSchema = z.object({
  turnId: z.string().min(1),
  chatId: z.string().min(1),
  botId: z.string().min(1),
  botUsername: z.string().min(2),
  testerName: z.string().nullable(),
  channelKey: z.string(),
  channelRu: z.string(),
  /** Сообщения клиента серии хода. */
  client: z.array(z.string()),
  /** Ответ Анны по частям. */
  anna: z.array(z.string()),
  correction: z.string(),
  state: TestCorrectionStateSchema,
  stateRu: z.string().min(1),
  at: MomentSchema,
  exampleId: z.string().nullable(),
});
export type TestCorrectionRow = z.infer<typeof TestCorrectionRowSchema>;

/** Правки одного состояния, свежие сверху. */
export const TestCorrectionsResponseSchema = z.object({ items: z.array(TestCorrectionRowSchema) });
export type TestCorrectionsResponse = z.infer<typeof TestCorrectionsResponseSchema>;

/** «В примеры»: текст ответа, как его оставил человек (обычно — исправление тестировщика). */
export const TestBotExampleBodySchema = z.object({ reply: z.string() });
export type TestBotExampleBody = z.infer<typeof TestBotExampleBodySchema>;

export const TestBotExampleResponseSchema = z.object({ exampleId: z.string().min(1), undoId: z.string().min(1) });
export type TestBotExampleResponse = z.infer<typeof TestBotExampleResponseSchema>;

/** «Не в примеры» и обратно; «сохранено» ставит только «В примеры». */
export const CORRECTION_STATES_BY_HAND = ['new', 'dismissed'] as const;
export const CorrectionStateBodySchema = z.object({ state: z.enum(CORRECTION_STATES_BY_HAND) });
export type CorrectionStateBody = z.infer<typeof CorrectionStateBodySchema>;

export const CorrectionStateResponseSchema = z.object({
  turnId: z.string().min(1),
  state: TestCorrectionStateSchema,
  stateRu: z.string().min(1),
});
export type CorrectionStateResponse = z.infer<typeof CorrectionStateResponseSchema>;

// ───────────────────────────── экзамен ─────────────────────────────

/** Случай экзамена из бота: когда, канал, эталон, серия клиента и ответ Анны. */
export const TestExamCaseRowSchema = z.object({
  id: z.string().min(1),
  turnId: z.string().min(1),
  botId: z.string().min(1),
  botUsername: z.string().min(2),
  channelKey: z.string(),
  channelRu: z.string(),
  referenceKind: TestExamReferenceKindSchema,
  referenceKindRu: z.string().min(1),
  /** Эталон по абзацам: одобренный ответ Анны или исправление. */
  reference: z.array(z.string()),
  /** Сообщения клиента серии. */
  client: z.array(z.string()),
  annaParts: z.array(z.string()),
  /** Какое время видела Анна (часы песочницы). */
  worldAt: MomentSchema,
  createdAt: MomentSchema,
  testerName: z.string().nullable(),
  status: TestExamStatusSchema,
  statusRu: z.string().min(1),
  archivedAt: MomentSchema.nullable(),
  /** Кто убрал в архив: `staff:<id>` из пульта, `tester:<id>` из бота. */
  archivedBy: z.string().nullable(),
});
export type TestExamCaseRow = z.infer<typeof TestExamCaseRowSchema>;

export const TestExamCasesResponseSchema = z.object({ items: z.array(TestExamCaseRowSchema) });
export type TestExamCasesResponse = z.infer<typeof TestExamCasesResponseSchema>;

export const ExamCaseStatusBodySchema = z.object({ status: TestExamStatusSchema });
export type ExamCaseStatusBody = z.infer<typeof ExamCaseStatusBodySchema>;

export const ExamCaseStatusResponseSchema = z.object({ case: TestExamCaseRowSchema });
export type ExamCaseStatusResponse = z.infer<typeof ExamCaseStatusResponseSchema>;

// ───────────────────────────── ошибки ─────────────────────────────

/**
 * Коды ошибок раздела и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`). Пять своих и общие пульта:
 * тело не того вида, нет такой записи, запретное в примере, эмбеддингов нет, отмена не применилась.
 */
export const TESTBOT_ERROR_STATUS = {
  /** Нет ключа шифрования токенов (`AI_QUAL_TESTBOT_KEY`): подключать ботов и менять токен нельзя. */
  testbot_key_missing: 409,
  /** Telegram не принял токен. */
  testbot_token_rejected: 400,
  /** Telegram не ответил. */
  telegram_unavailable: 502,
  /** Этот Telegram-бот уже подключён. */
  testbot_exists: 409,
  /** Новый токен — от другого бота. */
  testbot_token_mismatch: 400,
  bad_request: 400,
  not_found: 404,
  example_forbidden: 400,
  embeddings_unavailable: 503,
  undo_conflict: 409,
} as const;
export type TestbotErrorCode = keyof typeof TESTBOT_ERROR_STATUS;

/** Постоянные тексты своих ошибок (копия `API_ERRORS_RU`). */
export const TESTBOT_ERRORS_RU = {
  testbot_key_missing: 'Сервис не видит ключ шифрования токенов — администратор сервера задаёт его частью base. Пока ключа нет, ботов не подключить.',
  testbot_token_rejected: 'Telegram не принял токен: скопируйте его из @BotFather целиком, без пробелов внутри',
  telegram_unavailable: 'Telegram сейчас не отвечает, токен не проверен. Попробуйте через минуту',
  testbot_exists: 'Этот бот уже подключён — найдите его в списке ботов',
  testbot_token_mismatch: 'Это токен другого бота. Заменить можно только токен этого же бота из @BotFather',
} as const;

/** Тексты отказов в запросах раздела (`bad_request`, `not_found`). */
export const TESTBOT_REQUEST_ERRORS_RU = {
  createBodyShape: 'Тело запроса — JSON вида {"token": "…", "password": "…", "dailyBudgetRub": 300, "defaultChannel": "textback:tg"}.',
  patchBodyShape: 'Тело запроса — JSON с полями enabled, dailyBudgetRub, defaultChannel или autoEval.',
  nothingToPatch: 'Нечего менять: укажите хотя бы одно поле',
  passwordBodyShape: 'Тело запроса — JSON вида {"password": "…", "revokeAll": false}.',
  tokenBodyShape: 'Тело запроса — JSON вида {"token": "…"}.',
  tokenShape: 'Токен выглядит как 123456789:AAE… — скопируйте его из @BotFather целиком',
  passwordTooShort: `Пароль слишком короткий: нужно не меньше ${String(TEST_PASSWORD_LIMITS.min)} знаков`,
  passwordTooLong: `Пароль слишком длинный: можно не больше ${String(TEST_PASSWORD_LIMITS.max)} знаков`,
  // Разряды — неразрывным пробелом (записан кодом: невидимый знак в исходнике не отличить от пробела).
  budget: 'Лимит в день — целое число рублей от 1 до 100\u00a0000',
  channel: `Канал по умолчанию — один из: ${TEST_BOT_CHANNELS.join(', ')}`,
  chatsQuery: 'bot и tester — id из списков пульта, bad — 1, limit — число от 1 до 100.',
  badCursor: 'Курсор списка испорчен — откройте список заново',
  correctionsQuery: 'state — new, saved или dismissed.',
  examQuery: 'status — active или archived.',
  exampleBodyShape: 'Тело запроса — JSON вида {"reply": "текст ответа"}.',
  correctionStateBodyShape: 'Тело запроса — JSON вида {"state": "dismissed"}: new — вернуть в разбор, dismissed — не в примеры. «Сохранено» ставит только «В примеры».',
  noCorrection: 'У этого хода нет исправления',
  examStatusBodyShape: 'Тело запроса — JSON вида {"status": "archived"}: active или archived.',
  botNotFound: 'Бот не найден',
  testerNotFound: 'Тестировщик не найден',
  chatNotFound: 'Переписка не найдена',
  turnNotFound: 'Ход не найден',
  examCaseNotFound: 'Случай экзамена не найден',
} as const;
